import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Button, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CasinoIcon from '@mui/icons-material/Casino';
import LogoutIcon from '@mui/icons-material/Logout';
import StatusBar from './components/driver-demo/StatusBar';
import NotificationBanner from './components/driver-demo/NotificationBanner';
import BottomNav from './components/driver-demo/BottomNav';
import { getFriendlyName, getDrvLabel } from './components/driver-demo/utils';
import { computeUnassignedTiles } from './utils/boardTileProcessor';
import SAMPLE_RUN from './components/driver-demo/sampleRun';
import HomeScreen from './components/driver-demo/screens/HomeScreen';
import AssignmentDetailScreen from './components/driver-demo/screens/AssignmentDetailScreen';
import AtGateScreen from './components/driver-demo/screens/AtGateScreen';
import DownloadModeScreen from './components/driver-demo/screens/DownloadModeScreen';
import DownloadReportScreen from './components/driver-demo/screens/DownloadReportScreen';
import DriveModeScreen from './components/driver-demo/screens/DriveModeScreen';
import EndRunScreen from './components/driver-demo/screens/EndRunScreen';
import MessagesScreen from './components/driver-demo/screens/MessagesScreen';
import ProfileScreen from './components/driver-demo/screens/ProfileScreen';
import RunSummaryScreen from './components/driver-demo/screens/RunSummaryScreen';
import { COLORS, DEVICE, SCREEN_VIEWPORT } from './components/driver-demo/styles';

// All screens the driver session can be in.
const STATE = {
  HOME: 'home',
  ASSIGNMENT_DETAIL: 'assignment_detail',
  AT_GATE: 'at_gate',
  DOWNLOAD: 'download',
  DOWNLOAD_REPORT: 'download_report',
  DRIVE: 'drive',
  END_RUN: 'end_run',
  MESSAGES: 'messages',
  PROFILE: 'profile',
  RUN_SUMMARY: 'run_summary',
};

// Run-state screens (used to detect "in run" for the home resume card).
const IN_RUN_STATES = new Set([
  STATE.ASSIGNMENT_DETAIL, STATE.AT_GATE, STATE.DOWNLOAD,
  STATE.DOWNLOAD_REPORT, STATE.DRIVE, STATE.END_RUN,
]);

const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// Build the message threads visible to the driver right now.
// One coordination thread for the INBOUND flight that's been pushed —
// it pops up when the assignment is pushed and naturally winds down ~1hr
// after the last driver completes the run (we tear it down at end-of-run
// for the demo).
const buildThreads = (inbound, includeFlightThread) => {
  const result = [
    { id: 'dispatcher', label: 'Dispatcher', kind: 'dispatcher' },
  ];
  if (includeFlightThread && inbound?.flightNumber) {
    result.push({
      id: `flight_${inbound.flightNumber}`,
      label: inbound.from ? `${inbound.flightNumber} from ${inbound.from}` : inbound.flightNumber,
      sublabel: inbound.eta && inbound.gate ? `ETA ${inbound.eta} · Gate ${inbound.gate}` : undefined,
      kind: 'flight',
      flight: inbound,
    });
  }
  result.push({ id: 'system', label: 'System', kind: 'system' });
  return result;
};

// Seed only the threads always present (Dispatcher + System).
const seedBaseMessages = () => ({
  dispatcher: [
    { sender: 'dispatcher', senderName: 'Dispatch', body: 'Morning Herb — busy bank coming. Stay tight on the C40 hots.', time: '06:14', read: true },
  ],
  system: [
    { sender: 'system', body: 'Run roster published for AM shift', time: '05:45', read: true },
    { sender: 'system', body: 'Inbound 1139 expected on time', time: '06:10', read: true },
  ],
});

// Default solver params used when loading a random demo (no dispatcher
// preferences to draw from). Mirror the flight-data-page defaults.
const DEMO_PARAMS = {
  isPriorityFlight: false,
  hasFreightManifest: false,
  hotThreshold: 30,
  coldThresholdHours: 2,
  flightNote: '',
};

// Pulls a random fixture from the dev DB, runs it through the tile processor
// + Magic Button solver, and returns a payload shaped like what the dispatch
// board's 📱 button stashes. Returns null on any failure.
async function fetchRandomDemoPayload() {
  // 1. Get a list of high-tile-count flights and pick one randomly.
  const topRes = await fetch('/api/dev/flights/top/tiles?limit=50');
  if (!topRes.ok) throw new Error('Could not load top flights');
  const top = await topRes.json();
  if (!Array.isArray(top) || top.length === 0) throw new Error('No fixtures available');
  const pick = top[Math.floor(Math.random() * top.length)];

  // 2. Fetch the parsed TDR + Brock data.
  const detailRes = await fetch(`/api/dev/flights/${pick.id}`);
  if (!detailRes.ok) throw new Error('Could not load fixture detail');
  const detail = await detailRes.json();
  const tdrData = detail.parsed_tdr_data;
  const brockData = detail.parsed_brock_data;
  if (!tdrData || !brockData) throw new Error('Fixture missing parsed data');

  const inboundETA = tdrData?.inbound?.eta || '';
  const inboundGate = tdrData?.inbound?.arrivalGate || '';
  const inboundFlightNumber = tdrData?.inbound?.flightNumber || '';
  const inboundFrom = tdrData?.inbound?.arrivingFrom || '';

  // 3. Process raw outbound flights into the same tile shape the dispatch
  //    board produces, then send to the Magic Button solver.
  const tiles = computeUnassignedTiles({
    tdrData, brockData,
    params: DEMO_PARAMS,
    inboundETA, inboundGate,
    splitMode: false,
  });

  const solveRes = await fetch('/api/solve-routes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      flights: tiles,
      inboundETA,
      inboundGate,
      params: DEMO_PARAMS,
      splitMode: false,
      flightNote: '',
    }),
  });
  if (!solveRes.ok) throw new Error('Solver call failed');
  const result = await solveRes.json();
  const drivers = (result.drivers || []).filter(d => !d.isLocalDriver && d.flights?.length > 0);
  if (drivers.length === 0) throw new Error('Solver produced no usable drivers');

  // Pick the busiest driver — usually the most demo-worthy.
  const driver = drivers.reduce((best, d) => (d.flights.length > best.flights.length ? d : best), drivers[0]);
  const otherDriverCount = drivers.length - 1;

  return {
    driver,
    inboundETA,
    inboundGate,
    inboundFlightNumber,
    inboundFrom,
    otherDriverCount,
    savedAt: Date.now(),
  };
}

function DriverDemoPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // Mobile = render the screen content full-viewport with no device frame
  // chrome. Desktop = wrap in the Zebra device frame with the event log.
  const isMobile = useMediaQuery('(max-width: 600px)');
  // Prefer fresh location.state from a same-tab navigate. Otherwise fall back
  // to the most recently stashed payload, then to a random fixture loaded
  // from the dev DB.
  const stashedPayload = useMemo(() => {
    try {
      const raw = localStorage.getItem('driverDemoPayload');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const [randomPayload, setRandomPayload] = useState(null);
  const [loadingRandom, setLoadingRandom] = useState(false);
  const [randomError, setRandomError] = useState(null);

  const loadRandomDemo = useCallback(async () => {
    setLoadingRandom(true);
    setRandomError(null);
    try {
      const payload = await fetchRandomDemoPayload();
      // In-memory only — don't write to localStorage. The stashed-payload
      // slot is reserved for the dispatch board 📱 button hand-off.
      setRandomPayload(payload);
    } catch (e) {
      setRandomError(e.message || 'Could not load a random demo');
    } finally {
      setLoadingRandom(false);
    }
  }, []);

  // Random demo is opt-in: user clicks the 🎲 button when they want
  // a fresh fixture. The default visit shows the canonical SAMPLE_RUN
  // so the demo is predictable in any environment.

  // Source priority:
  //   1) location.state — fresh navigate from a same-tab click
  //   2) stashed payload — but only if it was saved in the last 30s
  //      (handles the new-tab-from-📱-button hand-off; older stashes
  //       shouldn't shadow the canonical sample on later nav visits)
  //   3) randomPayload — explicitly loaded via the Random Demo button (dev)
  //   4) SAMPLE_RUN — baked-in canonical demo, always works
  const stashedIsFresh = stashedPayload && (Date.now() - (stashedPayload.savedAt || 0)) < 30000;
  const {
    driver,
    inboundETA,
    inboundGate,
    inboundFlightNumber,
    inboundFrom,
    otherDriverCount = 0,
  } = location.state
      || (stashedIsFresh ? stashedPayload : null)
      || randomPayload
      || SAMPLE_RUN;

  // ── Driver session state ───────────────────────────────────────────
  const [hasAssignment, setHasAssignment] = useState(false);
  const [activeState, setActiveState] = useState(STATE.HOME);
  // Tracks where the driver is in the run when they navigate back to home
  // mid-flight. Lets the home screen show a "resume" card.
  const [runStage, setRunStage] = useState(null);
  const [downloadCounts, setDownloadCounts] = useState({});
  const [currentStopIdx, setCurrentStopIdx] = useState(0);
  const [eventLog, setEventLog] = useState([]);
  const [notification, setNotification] = useState(null);
  const inbound = useMemo(() => ({
    flightNumber: inboundFlightNumber,
    from: inboundFrom,
    eta: inboundETA,
    gate: inboundGate,
  }), [inboundFlightNumber, inboundFrom, inboundETA, inboundGate]);

  const threads = useMemo(
    () => buildThreads(inbound, hasAssignment),
    [inbound, hasAssignment]
  );
  const [messagesByThread, setMessagesByThread] = useState(seedBaseMessages);

  // Seed any newly-visible thread that doesn't have messages yet (i.e. the
  // per-flight threads when an assignment is pushed). We only ADD new
  // threads here; we never wipe existing messages on this effect.
  useEffect(() => {
    setMessagesByThread((prev) => {
      let next = prev;
      threads.forEach((t) => {
        if (prev[t.id]) return;
        if (t.kind === 'flight') {
          if (next === prev) next = { ...prev };
          const fromPart = t.flight.from ? ` from ${t.flight.from}` : '';
          next[t.id] = [
            { sender: 'system', body: `Coordination thread opened — ${t.flight.flightNumber}${fromPart}`, time: now(), read: true },
          ];
        }
      });
      return next;
    });
  }, [threads]);
  const [viewedRun, setViewedRun] = useState(null);
  // When the driver opens the assignment detail mid-run (e.g. from drive
  // mode to peek at the summary), this stores where to return on back.
  const [summaryReturnTo, setSummaryReturnTo] = useState(null);

  const totalUnread = Object.values(messagesByThread).reduce(
    (sum, msgs) => sum + msgs.filter(m => !m.read && m.sender !== 'driver').length,
    0
  );

  const logEvent = useCallback((text) => {
    setEventLog((prev) => [...prev, { time: new Date().toLocaleTimeString(), text }]);
  }, []);

  // Reset everything when driver changes (re-entry from a different driver)
  useEffect(() => {
    setHasAssignment(false);
    setActiveState(STATE.HOME);
    setRunStage(null);
    setDownloadCounts({});
    setCurrentStopIdx(0);
    setEventLog([]);
    setNotification(null);
    setMessagesByThread(seedBaseMessages());
  }, [driver?.id]);

  // Universal "go home" — preserves run state so the home screen can show
  // an "in progress · tap to resume" card. Only stores in-run screens as the
  // resume target; navigating away from messages/profile shouldn't trigger
  // the resume card.
  const goHome = () => {
    if (IN_RUN_STATES.has(activeState)) {
      setRunStage(activeState);
    }
    setActiveState(STATE.HOME);
  };

  // Resume the run from home → jumps back to wherever the driver was.
  const resumeRun = () => {
    if (runStage) {
      setActiveState(runStage);
      setRunStage(null);
    }
  };

  // ── Simulator triggers (from dispatcher side) ─────────────────────
  const simulatePushAssignment = () => {
    if (hasAssignment) return;
    setHasAssignment(true);
    setNotification({
      type: 'assignment',
      title: 'New Assignment',
      body: `Inbound run · ${driver.flights.length} stops · ${driver.flights.reduce((t, f) => t + (f.bags || 0), 0)} bags`,
    });
    logEvent('Dispatch pushed assignment to driver');
  };

  const simulatePlaneInRange = () => {
    setNotification({
      type: 'plane',
      title: 'Aircraft 10 min out',
      body: `Inbound to gate ${inboundGate || '?'} — head over and prep for download.`,
    });
    logEvent('Plane-in-range alert sent (10 min out)');
  };

  const simulateMessage = () => {
    const body = 'Heads up — possible delay on the C44 connection. Watch for the gate change.';
    setNotification({
      type: 'message',
      title: 'Message from Dispatcher',
      body,
    });
    setMessagesByThread((prev) => ({
      ...prev,
      dispatcher: [...prev.dispatcher, { sender: 'dispatcher', senderName: 'Dispatch', body, time: now(), read: false }],
    }));
    logEvent('Dispatcher sent a message');
  };

  // Messaging actions ─────────────────────────────────────────────
  const markThreadRead = (threadId) => {
    setMessagesByThread((prev) => ({
      ...prev,
      [threadId]: (prev[threadId] || []).map(m => ({ ...m, read: true })),
    }));
  };

  const sendReply = (threadId, body) => {
    setMessagesByThread((prev) => ({
      ...prev,
      [threadId]: [...(prev[threadId] || []), { sender: 'driver', body, time: now(), read: true }],
    }));
    logEvent(`Driver sent reply to ${threadId}`);
  };

  // Routes a driver-typed note (from the download report or end-of-run
  // screens) into the per-flight coordination thread. Dispatch can read
  // it inline with the rest of the flight's chatter.
  const postNoteToFlightThread = (note) => {
    if (!inboundFlightNumber) return;
    const threadId = `flight_${inboundFlightNumber}`;
    setMessagesByThread((prev) => {
      if (!prev[threadId]) return prev;
      return {
        ...prev,
        [threadId]: [
          ...prev[threadId],
          { sender: 'driver', body: note, time: now(), read: true },
        ],
      };
    });
  };

  const simulateDriving = () => {
    if (activeState !== STATE.DRIVE) {
      setActiveState(STATE.DRIVE);
      logEvent('Driver entered drive mode (GPS active)');
    } else {
      logEvent('Drive simulation ping — moving to next gate');
    }
  };

  // ── Driver-side actions (within the device) ────────────────────────
  const acknowledgeAssignment = () => {
    setActiveState(STATE.ASSIGNMENT_DETAIL);
    setRunStage(null);
    setSummaryReturnTo(null);
    logEvent('Driver acknowledged assignment');
  };

  // Open the assignment detail as a read-only summary while mid-run.
  // The bottom button switches to "Back to Drive" instead of "I'm Ready".
  const viewSummaryFromCurrentScreen = () => {
    setSummaryReturnTo(activeState);
    setActiveState(STATE.ASSIGNMENT_DETAIL);
    logEvent('Driver opened run summary');
  };

  const exitSummary = () => {
    const target = summaryReturnTo || STATE.HOME;
    setSummaryReturnTo(null);
    setActiveState(target);
  };

  const driverReady = () => {
    setActiveState(STATE.AT_GATE);
    logEvent('Driver heading to gate (work mode)');
  };

  const arrivedAtGate = () => {
    setActiveState(STATE.DOWNLOAD);
    logEvent(`Driver checked in at gate ${inboundGate || '?'} — geofenced ✓`);
  };

  const incrementBag = (flightId) => {
    setDownloadCounts((prev) => ({ ...prev, [flightId]: (prev[flightId] || 0) + 1 }));
  };
  const decrementBag = (flightId) => {
    setDownloadCounts((prev) => ({ ...prev, [flightId]: Math.max(0, (prev[flightId] || 0) - 1) }));
  };
  const scanBag = (flightId) => {
    setDownloadCounts((prev) => ({ ...prev, [flightId]: (prev[flightId] || 0) + 1 }));
    logEvent(`Bag scanned for ${(driver.flights.find(f => f.uniqueId === flightId)?.flightNumber) || '?'}`);
  };

  const completeDownload = () => {
    setActiveState(STATE.DOWNLOAD_REPORT);
    logEvent('Download phase complete — report generated');
  };

  const startDriving = () => {
    setActiveState(STATE.DRIVE);
    setCurrentStopIdx(0);
    logEvent('Drive mode started — heading to first stop');
  };

  const arriveAtStop = () => {
    const stop = driver.flights[currentStopIdx];
    if (stop) {
      logEvent(`Arrived at gate ${stop.gate} — bags dropped (${stop.flightNumber})`);
    }
    setCurrentStopIdx((idx) => idx + 1);
  };

  const endRun = () => {
    setActiveState(STATE.END_RUN);
    logEvent('Run ended — driver back to home');
  };

  const backToHome = () => {
    setActiveState(STATE.HOME);
    setHasAssignment(false);
    setRunStage(null);
    setDownloadCounts({});
    setCurrentStopIdx(0);
    logEvent('Returned to home — ready for next assignment');
  };

  // ── Loading / empty / error states ────────────────────────────────
  if (!driver) {
    if (loadingRandom) {
      return (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: 'calc(100vh - 64px)', gap: 2, color: COLORS.textPrimary,
        }}>
          <Typography sx={{ fontSize: '1rem', color: COLORS.accent, fontWeight: 600 }}>
            🎲 Loading random demo…
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: COLORS.textMuted, textAlign: 'center', maxWidth: 480 }}>
            Pulling a fixture from the dev DB and running it through the solver.
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: 'calc(100vh - 64px)', gap: 2, color: COLORS.textPrimary,
      }}>
        <Typography sx={{ fontSize: '1.1rem', color: COLORS.textSecondary }}>
          {randomError ? 'Couldn\'t load a random demo.' : 'No driver loaded.'}
        </Typography>
        {randomError && (
          <Typography sx={{ fontSize: '0.8rem', color: COLORS.textMuted, textAlign: 'center', maxWidth: 480 }}>
            {randomError} — make sure Flask is running with dev fixtures enabled.
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
          {process.env.NODE_ENV === 'development' && (
            <Button variant="contained" onClick={loadRandomDemo} startIcon={<CasinoIcon />}
              sx={{ backgroundColor: COLORS.accent, color: '#0a1020', '&:hover': { backgroundColor: COLORS.accentDim } }}>
              Try Random Demo
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate('/breakdown')} sx={{ color: COLORS.accent, borderColor: COLORS.accent }}>
            Back to Dispatch
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Pick the active screen ────────────────────────────────────────
  const renderScreen = () => {
    switch (activeState) {
      case STATE.HOME:
        return (
          <HomeScreen
            driver={driver}
            inboundETA={inboundETA}
            inboundGate={inboundGate}
            otherDriverCount={otherDriverCount}
            hasAssignment={hasAssignment}
            runStage={runStage}
            isLate={false}
            onOpenAssignment={acknowledgeAssignment}
            onResumeRun={resumeRun}
            onOpenPreviousRun={(run) => {
              setViewedRun(run);
              setActiveState(STATE.RUN_SUMMARY);
              logEvent(`Opened previous run summary: ${run.flightNumber}`);
            }}
          />
        );
      case STATE.RUN_SUMMARY:
        return (
          <RunSummaryScreen
            run={viewedRun}
            onBack={() => setActiveState(STATE.HOME)}
          />
        );
      case STATE.ASSIGNMENT_DETAIL: {
        const isSummaryMode = !!summaryReturnTo;
        return (
          <AssignmentDetailScreen
            driver={driver}
            inboundETA={inboundETA}
            inboundGate={inboundGate}
            mode={isSummaryMode ? 'summary' : 'ack'}
            onBack={isSummaryMode ? exitSummary : () => setActiveState(STATE.HOME)}
            onReady={isSummaryMode ? exitSummary : driverReady}
          />
        );
      }
      case STATE.AT_GATE:
        return (
          <AtGateScreen
            inboundGate={inboundGate}
            onConfirmArrived={arrivedAtGate}
            onBack={() => setActiveState(STATE.ASSIGNMENT_DETAIL)}
          />
        );
      case STATE.DOWNLOAD:
        return (
          <DownloadModeScreen
            driver={driver}
            downloadCounts={downloadCounts}
            onIncrement={incrementBag}
            onDecrement={decrementBag}
            onScan={scanBag}
            onComplete={completeDownload}
          />
        );
      case STATE.DOWNLOAD_REPORT:
        return (
          <DownloadReportScreen
            driver={driver}
            downloadCounts={downloadCounts}
            onProceed={startDriving}
            onSaveNote={(note) => {
              postNoteToFlightThread(note);
              logEvent(`Driver posted note to flight thread (download): "${note}"`);
            }}
          />
        );
      case STATE.DRIVE:
        return (
          <DriveModeScreen
            driver={driver}
            currentStopIdx={currentStopIdx}
            onArriveAtStop={arriveAtStop}
            onEndRun={endRun}
            onViewSummary={viewSummaryFromCurrentScreen}
          />
        );
      case STATE.END_RUN:
        return (
          <EndRunScreen
            driver={driver}
            downloadCounts={downloadCounts}
            onAcknowledge={backToHome}
            onSaveNote={(note) => {
              postNoteToFlightThread(note);
              logEvent(`Driver posted note to flight thread (end-of-run): "${note}"`);
            }}
          />
        );
      case STATE.MESSAGES:
        return (
          <MessagesScreen
            threads={threads}
            messagesByThread={messagesByThread}
            onSendReply={sendReply}
            onMarkRead={markThreadRead}
          />
        );
      case STATE.PROFILE:
        return <ProfileScreen driver={driver} />;
      default:
        return <HomeScreen driver={driver} hasAssignment={hasAssignment} onOpenAssignment={acknowledgeAssignment} />;
    }
  };

  const friendlyName = getFriendlyName(driver.id);
  const drvLabel = getDrvLabel(driver.name);

  return (
    <Box sx={{
      minHeight: isMobile ? undefined : 'calc(100vh - 64px)',
      backgroundColor: '#0a1020',
      display: 'flex',
      flexDirection: 'column',
      // On mobile, take over the full visible viewport (covers the NavBar)
      // so the demo feels like a true standalone app. We rely on inset:0
      // instead of 100vh because mobile browsers report 100vh larger than
      // the visible area when the URL bar is showing — that hides the
      // bottom ribbon below the fold.
      ...(isMobile && {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 9999,
        // 100dvh accounts for mobile browser chrome (URL bar). Falls back
        // to 100vh in older browsers. Combined with bottom:0, ensures the
        // simulator ribbon AND the device's bottom nav both stay visible.
        height: '100dvh',
      }),
    }}>
      {/* Page header — desktop only. On mobile the user's phone IS the device. */}
      <Box sx={{
        display: isMobile ? 'none' : 'flex',
        alignItems: 'center', gap: 2,
        px: 3, py: 2,
        borderBottom: `1px solid ${COLORS.divider}`,
        backgroundColor: 'rgba(15, 23, 42, 0.8)',
      }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: COLORS.textSecondary }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Driver Scanner — Live Demo
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: COLORS.accent, lineHeight: 1.1 }}>
            {drvLabel} · {friendlyName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.65rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Run Snapshot
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: COLORS.textPrimary, fontWeight: 600 }}>
              {driver.flights.length} stops · {driver.flights.reduce((t, f) => t + (f.bags || 0), 0)} bags
            </Typography>
            {inboundETA && (
              <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>
                Inbound {inboundETA} · Gate {inboundGate || '?'}
              </Typography>
            )}
          </Box>
          {process.env.NODE_ENV === 'development' && (
            <Button
              onClick={loadRandomDemo}
              disabled={loadingRandom}
              variant="outlined"
              startIcon={<CasinoIcon />}
              sx={{
                color: COLORS.accent,
                borderColor: COLORS.accentDim,
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: COLORS.accent, backgroundColor: 'rgba(34,211,238,0.08)' },
              }}
            >
              {loadingRandom ? 'Loading…' : 'New Random Demo'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Body: device frame + event log on desktop, full-screen app on mobile */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        gap: isMobile ? 0 : 4,
        p: isMobile ? 0 : 4,
        alignItems: isMobile ? 'stretch' : 'flex-start',
        justifyContent: 'center',
        minHeight: 0,
      }}>
        {/* Device frame — chassis only on desktop */}
        <Box sx={{
          width: isMobile ? '100%' : DEVICE.width,
          height: isMobile ? '100%' : DEVICE.height,
          backgroundColor: isMobile ? 'transparent' : COLORS.deviceChassis,
          borderRadius: isMobile ? 0 : `${DEVICE.cornerRadius}px`,
          padding: isMobile ? 0 : `${DEVICE.bezel}px`,
          boxSizing: 'border-box',
          boxShadow: isMobile ? 'none' : '0 30px 80px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.05)',
          flexShrink: 0,
          position: 'relative',
        }}>
          <Box sx={{
            width: isMobile ? '100%' : SCREEN_VIEWPORT.width,
            height: isMobile ? '100%' : SCREEN_VIEWPORT.height,
            backgroundColor: COLORS.screenBg,
            borderRadius: isMobile ? 0 : `${DEVICE.innerCornerRadius}px`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            // Hide scrollbars on every scroll container inside the device.
            // Touch users know they can scroll; visible scrollbars feel un-app-like.
            '& *': {
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            },
            '& *::-webkit-scrollbar': {
              display: 'none',
              width: 0,
              height: 0,
            },
          }}>
            {/* Fake Zebra status bar — hide on mobile so it doesn't fight the real one */}
            {!isMobile && <StatusBar />}
            <NotificationBanner
              notification={notification}
              onDismiss={() => setNotification(null)}
              onAction={(n) => {
                if (n.type === 'assignment') acknowledgeAssignment();
              }}
            />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {renderScreen()}
            </Box>
            <BottomNav
              active={
                activeState === STATE.HOME ? 'home' :
                activeState === STATE.MESSAGES ? 'messages' :
                activeState === STATE.PROFILE ? 'profile' :
                null
              }
              onHome={goHome}
              onMessages={() => {
                if (IN_RUN_STATES.has(activeState)) setRunStage(activeState);
                setActiveState(STATE.MESSAGES);
              }}
              onProfile={() => {
                if (IN_RUN_STATES.has(activeState)) setRunStage(activeState);
                setActiveState(STATE.PROFILE);
              }}
              unreadMessages={totalUnread}
            />
          </Box>
        </Box>

        {/* Event log — desktop only */}
        <Box sx={{
          display: isMobile ? 'none' : 'flex',
          width: 320,
          maxHeight: DEVICE.height,
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          border: `1px solid ${COLORS.divider}`,
          borderRadius: 2,
          p: 2,
          flexDirection: 'column',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Event Log
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: COLORS.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              State: {activeState.replace(/_/g, ' ')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {eventLog.length === 0 ? (
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted, fontStyle: 'italic' }}>
                No events yet. Use a simulator trigger below to start the demo.
              </Typography>
            ) : (
              eventLog.slice().reverse().map((e, i) => (
                <Box key={i} sx={{ borderLeft: `2px solid ${COLORS.accent}`, pl: 1, py: 0.25 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: COLORS.textMuted }}>{e.time}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: COLORS.textPrimary }}>{e.text}</Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>

      {/* Dispatcher simulator strip (footer ribbon) */}
      <Box sx={{
        display: 'flex',
        gap: isMobile ? 0.75 : 1.5,
        px: isMobile ? 1 : 4,
        py: isMobile ? 1 : 2,
        borderTop: `1px solid ${COLORS.divider}`,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        justifyContent: 'center',
        alignItems: 'center',
        overflowX: 'auto',
        flexShrink: 0,
        // Respect iPhone home indicator on mobile
        pb: isMobile ? 'max(env(safe-area-inset-bottom), 8px)' : 2,
      }}>
        {!isMobile && (
          <Typography sx={{
            fontSize: '0.65rem', color: COLORS.textMuted,
            textTransform: 'uppercase', letterSpacing: 0.6,
            alignSelf: 'center', mr: 1, whiteSpace: 'nowrap',
          }}>
            Dispatcher Simulator →
          </Typography>
        )}
        <SimButton compact={isMobile} label={isMobile ? '📲' : '📲 Push Assignment'} onClick={simulatePushAssignment} disabled={hasAssignment} />
        <SimButton compact={isMobile} label={isMobile ? '🛬' : '🛬 Plane In Range'} onClick={simulatePlaneInRange} />
        <SimButton compact={isMobile} label={isMobile ? '💬' : '💬 Send Message'} onClick={simulateMessage} />
        <SimButton compact={isMobile} label={isMobile ? '🚗' : '🚗 Simulate Driving'} onClick={simulateDriving} />
        <Box sx={{ flex: 1, minWidth: isMobile ? 4 : 8 }} />
        <Button
          onClick={() => navigate('/breakdown')}
          variant="outlined"
          size="small"
          startIcon={!isMobile && <LogoutIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            color: COLORS.textSecondary,
            borderColor: COLORS.divider,
            fontSize: isMobile ? '0.7rem' : '0.78rem',
            fontWeight: 600,
            px: isMobile ? 1.25 : 2,
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            '&:hover': { borderColor: COLORS.danger, color: COLORS.danger },
          }}
        >
          {isMobile ? 'Exit' : 'Exit Demo'}
        </Button>
      </Box>
    </Box>
  );
}

function SimButton({ label, onClick, disabled, compact = false }) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant="outlined"
      size="small"
      sx={{
        textTransform: 'none',
        color: disabled ? COLORS.textMuted : COLORS.accent,
        borderColor: disabled ? COLORS.divider : COLORS.accentDim,
        fontSize: compact ? '0.95rem' : '0.78rem',
        fontWeight: 600,
        px: compact ? 1.25 : 2,
        minWidth: compact ? 0 : 'auto',
        whiteSpace: 'nowrap',
        '&:hover': {
          borderColor: COLORS.accent,
          backgroundColor: 'rgba(34, 211, 238, 0.08)',
        },
      }}
    >
      {label}
    </Button>
  );
}

export default DriverDemoPage;
